<script setup>
import { computed } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';
import { FLOW_ACTION_TYPES } from '../constants';
import useAutomationValues from 'dashboard/composables/useAutomationValues';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { getActionDropdownValues } = useAutomationValues();
const isHovered = ref(false);
const isExpanded = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};

const resolveParamName = (actionName, params) => {
  if (!params || (Array.isArray(params) && params.length === 0)) return '';
  const ids = Array.isArray(params) ? params : [params];
  const dropdownValues = getActionDropdownValues(actionName) || [];
  const names = ids.map(id => {
    const match = dropdownValues.find(v => v.id === id);
    return match ? match.name : String(id);
  });
  return names.join(', ');
};

const actionItems = computed(() => {
  const actions = props.data?.actions || [];
  return actions
    .filter(a => a.action_name)
    .map(action => {
      const actionType = FLOW_ACTION_TYPES.find(
        a => a.key === action.action_name
      );
      if (!actionType) return { label: action.action_name, param: '' };
      const summaryKey = `FLOW_BUILDER.NODE_EDITOR.ACTION.SUMMARY.${actionType.label}`;
      const translated = t(summaryKey);
      const label =
        translated === summaryKey
          ? t(`AUTOMATION.ACTIONS.${actionType.label}`)
          : translated;
      const paramStr = resolveParamName(
        action.action_name,
        action.action_params
      );
      return {
        label,
        param: paramStr.length > 15 ? `${paramStr.slice(0, 15)}...` : paramStr,
      };
    });
});

const actionsCount = computed(() => actionItems.value.length);

const actionsSummary = computed(() => {
  if (actionsCount.value === 0) {
    return t('FLOW_BUILDER.NODE_EDITOR.ACTION.SELECT_ACTION');
  }
  return t('FLOW_BUILDER.NODE_EDITOR.ACTION.ACTIONS_COUNT', {
    count: actionsCount.value,
  });
});

const visibleActions = computed(() =>
  isExpanded.value ? actionItems.value : actionItems.value.slice(0, 2)
);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="flow-node min-w-[180px] bg-white dark:bg-n-slate-1 rounded-[10px] p-2 text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-green-500 shadow-lg'
        : 'border border-transparent hover:border-n-blue-8'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      </div>
    </NodeToolbar>
    <Handle
      type="target"
      :position="Position.Left"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_INPUT')"
    />
    <div class="flex items-center gap-2">
      <span class="i-lucide-settings text-[10px] text-n-violet-10" />
      <div class="flex flex-col">
        <span class="font-semibold text-n-slate-12 text-[9px]">
          {{ props.data?.label || t('FLOW_BUILDER.SIDEBAR.ACTION') }}
        </span>
        <span class="text-[8px] text-n-slate-11">
          {{ actionsSummary }}
        </span>
      </div>
    </div>
    <div class="mt-1.5 px-1 flex flex-col gap-0.5">
      <template v-if="visibleActions.length > 0">
        <span
          v-for="(item, i) in visibleActions"
          :key="i"
          class="text-[7px] text-n-slate-11 leading-tight truncate max-w-[150px]"
        >
          {{ item.label }}<span v-if="item.param">: {{ item.param }}</span>
        </span>
      </template>
      <span v-else class="text-[7px] text-n-slate-10 leading-tight italic">
        {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.SELECT_ACTION') }}
      </span>
      <button
        v-if="actionsCount > 2"
        class="text-[7px] text-n-blue-9 font-medium leading-tight flex items-center gap-0.5 hover:text-n-blue-10 transition-colors"
        @click.stop="toggleExpand"
      >
        <span
          :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="text-[8px]"
        />
        {{
          isExpanded
            ? t('FLOW_BUILDER.NODE_EDITOR.CONDITION.SHOW_LESS')
            : t('FLOW_BUILDER.NODE_EDITOR.CONDITION.SHOW_ALL')
        }}
      </button>
    </div>
    <Handle
      type="source"
      :position="Position.Right"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_OUTPUT')"
    />
  </div>
</template>
